import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link } from 'react-router-dom'

/**
 * A node in the peek navigation stack. `type` selects which detail
 * component renders it; the owning screen maps `type` → component +
 * full-page path. `id` is the entity id. `label` is the short display
 * name for the breadcrumb (falls back to the type label when absent).
 */
export interface PeekEntry {
  readonly type: string
  readonly id: string
  readonly label?: string
}

interface PeekNavValue {
  /** Drill into a linked entity within the same peek panel. */
  readonly push: (entry: PeekEntry) => void
}

const PeekNavContext = createContext<PeekNavValue | null>(null)

export const PeekNavProvider = PeekNavContext.Provider

/**
 * Consumed by detail components (via {@link EntityRef}) to drill into a
 * linked entity inside the current peek. Returns `null` when rendered
 * full-page, so the same component navigates normally outside a peek.
 */
export function usePeekNav(): PeekNavValue | null {
  return useContext(PeekNavContext)
}

export interface PeekStack {
  /** The entry to render — the top of the drill-down stack, or the root. */
  readonly current: PeekEntry | null
  /** Full trail root → current, for the breadcrumb. */
  readonly path: readonly PeekEntry[]
  /** True when there's a drill-down to pop back from. */
  readonly canGoBack: boolean
  readonly push: (entry: PeekEntry) => void
  readonly back: () => void
  /** Jump to a breadcrumb index (0 = root); truncates the trail. */
  readonly goTo: (index: number) => void
}

function sameEntry(a: PeekEntry, b: PeekEntry): boolean {
  return a.type === b.type && a.id === b.id
}

/**
 * In-memory drill-down stack layered on a URL-backed `root` entry. The
 * root comes from the list's `?peek` selection; drilling into linked
 * entities pushes ephemeral entries on top. Selecting a different root
 * row resets the drill-downs.
 *
 * The trail is kept a **unique path**: drilling into an entity already in
 * it goes *back* to that entity (truncates) rather than pushing a
 * duplicate — so customer → car → that-customer collapses in one step
 * instead of growing forever.
 */
export function usePeekStack(root: PeekEntry | null): PeekStack {
  const [extra, setExtra] = useState<readonly PeekEntry[]>([])

  const rootKey = root ? `${root.type}:${root.id}` : null
  const prevRootKey = useRef(rootKey)
  // Reset drill-downs whenever the root changes. Done during render (the
  // React "adjust state when a prop changes" pattern) so there's no
  // one-frame flash of a stale drilled-in entity after switching rows.
  if (prevRootKey.current !== rootKey) {
    prevRootKey.current = rootKey
    if (extra.length) setExtra([])
  }

  // Latest root for the dedup check, without making `push` depend on it
  // (root is a fresh object each render).
  const rootRef = useRef(root)
  rootRef.current = root

  const push = useCallback((entry: PeekEntry) => {
    setExtra((s) => {
      const r = rootRef.current
      // Revisiting the root collapses the whole drill-down.
      if (r && sameEntry(r, entry)) return []
      // Revisiting an entry already in the trail goes back to it
      // (truncate, keeping it as the tail) instead of duplicating.
      const idx = s.findIndex((e) => sameEntry(e, entry))
      if (idx >= 0) return s.slice(0, idx + 1)
      return [...s, entry]
    })
  }, [])

  const back = useCallback(() => setExtra((s) => s.slice(0, -1)), [])

  const goTo = useCallback(
    (index: number) => setExtra((s) => (index <= 0 ? [] : s.slice(0, index))),
    [],
  )

  const path = root ? [root, ...extra] : []
  // The `path.length` guard guarantees the indexed access is defined.
  const current = path.length ? path[path.length - 1]! : null
  return { current, path, canGoBack: extra.length > 0, push, back, goTo }
}

/**
 * Cross-entity reference. Inside a peek it pushes onto the nav stack
 * (drill in place); outside a peek it's an ordinary router `<Link>` to
 * the full page. Lets the same detail component serve both contexts.
 * `label` (the entity's display name) feeds the peek breadcrumb.
 */
export function EntityRef({
  type,
  id,
  label,
  to,
  className,
  children,
}: {
  readonly type: string
  readonly id: string
  readonly label?: string
  readonly to: string
  readonly className?: string
  readonly children: ReactNode
}) {
  const nav = usePeekNav()
  if (nav) {
    return (
      <button type="button" className={className} onClick={() => nav.push({ type, id, label })}>
        {children}
      </button>
    )
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  )
}
